import { KpiMetricType, RowCardMeta, RowCards, Typography } from '@weareopenr/component-library';
import { DashboardWidget } from './DashboardWidget';
import { EVENT_TYPES, EventType } from 'src/api/getEvents';
import { StatusChip } from 'src/components/StatusChip/StatusChip';
import { usePublishHistory } from 'src/hooks/usePublishHistory';
import { IPublishHistoryItem } from 'src/api/getPublishHistory';
import { ScheduleStatus } from 'src/api/getSchedules/types';
import { Routes } from 'src/routes';

import styles from './DashboardWidget.module.scss';

interface IRatio {
  numerator: number;
  denominator: number;
  /** Whole-number percentage, or null when there is nothing to measure. */
  percentage: number | null;
}

const ratio = (numerator: number, denominator: number): IRatio => {
  if (denominator === 0) {
    return { numerator, denominator, percentage: null };
  }
  return { numerator, denominator, percentage: Math.round((numerator / denominator) * 100) };
};

const PUBLISH_EVENT_TYPES: Set<EventType> = new Set([
  EVENT_TYPES.PUBLISH,
  EVENT_TYPES.PUBLISH_MENU,
  EVENT_TYPES.PUBLISH_PRODUCT,
  EVENT_TYPES.PUBLISH_PRICING,
]);

const MAX_EVENTS = 3;

const getPublishSuccessRate = (events: IPublishHistoryItem[]): IRatio => {
  const terminal: Set<ScheduleStatus> = new Set([
    ScheduleStatus.SUCCESS,
    ScheduleStatus.FAILURE,
    ScheduleStatus.PARTIAL_SUCCESS,
    ScheduleStatus.REJECTED,
  ]);
  const publishEvents = events.filter((event) => PUBLISH_EVENT_TYPES.has(event.event_type));
  const completed = publishEvents.filter((event) => terminal.has(event.status));
  const succeeded = completed.filter((event) => event.status === ScheduleStatus.SUCCESS);
  return ratio(succeeded.length, completed.length);
};

const publishTypeMap: Record<EventType, string> = {
  [EVENT_TYPES.PUBLISH]: 'Publish',
  [EVENT_TYPES.PUBLISH_MENU]: 'Menu publish',
  [EVENT_TYPES.PUBLISH_PRODUCT]: 'Product publish',
  [EVENT_TYPES.PUBLISH_PRICING]: 'Pricing publish',
  [EVENT_TYPES.DUPLICATE]: 'Duplicate',
  [EVENT_TYPES.EXPORT]: 'Export',
  [EVENT_TYPES.IMPORT]: 'Import',
};

export const RecentPublishesWidget = () => {
  const { data, isLoading } = usePublishHistory();

  const events = data?.pages.flatMap((page) => page.data) ?? [];

  const successRate = getPublishSuccessRate(events);

  const publishEvents = events
    .filter((event: IPublishHistoryItem) => PUBLISH_EVENT_TYPES.has(event.event_type))
    .sort((a, b) => new Date(b.updated_at).valueOf() - new Date(a.updated_at).valueOf())
    .slice(0, MAX_EVENTS);
  const isEmpty = publishEvents.length === 0;
  return (
    <DashboardWidget
      title="Recent Publish Activity"
      emptyCta={
        <div className={styles['widget-empty']}>
          <Typography size="medium" weight="semi-bold">
            No past publishes...
          </Typography>
          <Typography size="medium" weight="regular">
            Publish to see it appear here
          </Typography>
        </div>
      }
      link={{ label: 'View All', to: Routes.PUBLISH_HISTORY }}
      isEmpty={isEmpty}
      isLoading={isLoading}
      Kpis={[
        {
          id: 'recent-publishes-success-rate',
          size: 'small',
          value: isEmpty ? 'N/A' : successRate.percentage ?? 0,
          label: 'Success Rate',
          testId: 'kpi-recent-publishes-success-rate',
          metricType: isEmpty ? KpiMetricType.Text : KpiMetricType.Percentage,
        },
      ]}
    >
      <RowCards
        rows={publishEvents.map((event) => {
          const meta: RowCardMeta[] = [
            {
              key: 'updated_at',
              label: `Updated ${new Date(event.updated_at).toLocaleString()}`,
              colour: 'liquorice',
            },
            { key: 'event_type', label: publishTypeMap[event.event_type], colour: 'liquorice' },
          ];
          return {
            id: event.event_id,
            title: event.name ?? 'Untitled',
            meta: meta,
            chip: <StatusChip entity="publish" status={event.status} />,
            action: {
              label: 'View',
              to: `${Routes.PUBLISH_HISTORY}/status/${event.event_id}`,
            },
          };
        })}
      />
    </DashboardWidget>
  );
};
